import { Injectable } from '@angular/core';
import { Observable, Subject, map, race, take } from 'rxjs';
import { ConfirmModalData } from '../models/confirmation.models';

@Injectable({
  providedIn: 'root'
})
export class ConfirmHelperService {
  private openModel = new Subject<ConfirmModalData>();
  openModel$ = this.openModel.asObservable();

  private closeModel = new Subject<any>();
  closeModel$ = this.closeModel.asObservable();

  private confirmSubject = new Subject<void>();
  private rejectSubject = new Subject<void>();

  /**
   * Opens a confirmation modal and returns a scoped Observable<boolean>.
   *
   * How it works:
   * - `race()` listens to both confirmSubject and rejectSubject simultaneously.
   *   Whichever emits first wins — the other is automatically unsubscribed.
   *   Confirm maps to `true`, reject maps to `false`.
   * - `take(1)` ensures the observable completes after the first emission,
   *   so the caller's subscription is auto-cleaned. No manual unsubscribe
   *   or takeUntilDestroyed is needed.
   *
   * Precautions:
   * - Only one modal should be open at a time. If two components call
   *   showModal() simultaneously, both will race on the same subjects
   *   and the first confirm/reject will resolve both.
   * - Always subscribe to the returned observable — not to a shared
   *   global stream. This prevents cross-component leakage.
   */
  showModal(config: ConfirmModalData): Observable<boolean> {
    this.openModel.next(config);

    return race(this.confirmSubject.pipe(map(() => true)), this.rejectSubject.pipe(map(() => false))).pipe(take(1));
  }

  closeModal(data?: any) {
    this.closeModel.next(data);
  }

  emitConfirm() {
    this.confirmSubject.next();
  }

  emitReject() {
    this.rejectSubject.next();
  }
}
